/**
 * Shared constants for the users-groups feature.
 *
 * Names are prefixed with GROUPS_TREE_ so other users-groups subsystems can add
 * namespaced constants here without collision.
 */

/** Pixel width of a single indentation column in the groups tree. */
export const GROUPS_TREE_INDENT_WIDTH_PX = 24;

/** Maximum rendered indentation depth before connector lines stop stepping further right. */
export const GROUPS_TREE_MAX_VISUAL_DEPTH = 8;

/** Maximum number of admin monograms shown per row before collapsing into a `+N` chip. */
export const GROUPS_TREE_VISIBLE_ADMIN_CAP = 3;

/** Concrete pages that render the users/groups roster — revalidate BOTH after any
 * mutation. "/dashboard/organization/users" itself is a redirect-only stub
 * (see its page.tsx); revalidating it alone is a no-op for the pages that
 * actually render data, so every mutation here has relied on the client's own
 * `router.refresh()` to pick up changes. */
export const USERS_GROUPS_PAGES = [
  "/dashboard/organization/users/users",
  "/dashboard/organization/users/groups",
] as const;

/** Shown in place of a name when a person has neither a name nor an email on file. */
export const INVITED_USER_FALLBACK_NAME = "Invited user";

/** Debounce for the invite modal's org-member search (ms). */
export const INVITE_SEARCH_DEBOUNCE_MS = 300;

/** Maximum org_users rows scanned when resolving an org's member set for search — a
 * bound on query cost, not an expected real-world org size. */
export const INVITE_SEARCH_ORG_MEMBER_SCAN_LIMIT = 2000;

/** Maximum search results returned to the invite modal's dropdown. */
export const INVITE_SEARCH_RESULT_LIMIT = 50;

/** Maximum emails accepted in one invite submission (group or event) — a bound
 * on one request's blast radius, not an expected real-world invite batch size. */
export const MAX_BATCH_INVITE_SIZE = 20;

/** Maximum access rows fetched per query in the "Manage access" panel — a bound
 * on query cost against a group-derived grant that can fan out to hundreds of
 * members, not an expected real-world count. */
export const EVENT_ACCESS_LIST_MAX = 500;

/** Invite quota: the rolling window invites are counted over, and the ceilings
 * that apply inside it. Enforced by `nowadays.charge_invite_quota` against a
 * counter on the org row, not a per-process counter — the previous in-memory
 * limiter was invisible to every other Vercel instance, which let one org send
 * 917 invites in half an hour under a nominal cap of 50.
 *
 * One unit is one invited recipient: every address the product writes a user
 * row for or mails, a re-send included. Counting the rows an invite creates was
 * tried and abandoned, because a tenant can delete those (cancel an invite,
 * remove a member) and refund their own budget.
 *
 * The established-org ceiling is deliberately well above the old nominal 50:
 * that number was never actually enforced, so real orgs may already sit above
 * it, and this is the first build where exceeding it refuses the invite. */
export const INVITE_QUOTA_WINDOW_MS = 24 * 60 * 60 * 1000;
export const INVITE_QUOTA_MAX_PER_ORG = 200;

/** How long an org counts as new, and the much smaller invite ceiling that
 * applies while it does. Bulk invites in an org's first hours are the abuse
 * signature — a real customer onboarding their team stays well under this. */
export const NEW_ORG_TRUST_WINDOW_MS = 24 * 60 * 60 * 1000;
export const INVITE_QUOTA_MAX_PER_NEW_ORG = 25;

/** KNOWN GAP: there is no longer a per-invitee bound on resends.
 *
 * `resendRateLimiter` used to cap resends at 5 per invitee per hour. It was
 * removed because it was a `createRateLimiter`, i.e. process-local and therefore
 * unenforced across Vercel instances — but "unenforced" is not "unwanted", and
 * the anti-harassment property it encoded has nothing covering it now. The org
 * quota below is per-org, so one address can be re-mailed up to the org's whole
 * daily ceiling. Bounding it properly needs a counter keyed per invitee, which
 * is deliberately out of scope here. Tracked on ENG-872. */

/** Shared refusal copy, so every invite surface fails the same way. Lives here
 * rather than beside the quota logic because the action tests assert it. */
export const INVITE_QUOTA_EXHAUSTED_ERROR = "Too many invites sent recently. Please try again later.";

/** Cap on rows returned by the users/groups roster RPC — a bound on query cost
 * as the roster grows, not an expected real-world org size. */
export const ROSTER_MAX = 1000;

export const INVITE_EMAIL_FROM = "noreply@nowadays.ai";
export const INVITE_EMAIL_REPLY_TO = "support@nowadays.ai";

/** Max length (characters) for an optional personal note included in the invite email. */
export const MAX_INVITE_MESSAGE_LENGTH = 500;

/** Max other-group names / orphaned-event names / granted-event ids surfaced in the
 * remove-confirmation dialog — a bound on query cost for someone in an unusually
 * large number of groups, not an expected real-world count. */
export const REMOVAL_IMPACT_LIST_CAP = 50;

/** Max total access rows scanned per orphan check in the remove-confirmation dialog —
 * a bound on query cost for an event shared with a very large group, not an expected
 * real-world count. If the scan hits this cap, the orphan warning is suppressed rather
 * than risk a false "they're the only person with access" claim from a truncated count. */
export const ORPHAN_CHECK_ROW_CAP = 500;

/**
 * Query param that opens the invite modal on arrival.
 *
 * Owned here because it is this page's URL contract. Callers (the command
 * palette, any link we send someone) import it rather than hardcoding "invite",
 * so renaming it is a single edit with the compiler enforcing the rest.
 */
export const INVITE_PARAM = "invite";
